import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  ProgramProductTypeTag,
  TagConsolidationLegalityDto,
  TagConsolidationResultDto,
} from '@models';
import { UtilFns } from '@utils';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';

export interface IConsolidateTagsParams {
  // The full set of selected tags from the grid. User picks one as canonical via radio;
  // the rest become synonyms folded into it.
  selectedTags: ProgramProductTypeTag[];
}

export interface IConsolidateTagsResult {
  consolidated: boolean;
  canonicalTagId?: string;
}

@Component({
  selector: 'prox-consolidate-tags.dialog',
  standalone: false,
  templateUrl: './consolidate-tags.dialog.component.html',
})
export class ConsolidateTagsDialogComponent implements OnInit {
  isPageReady = false;
  isCheckingLegality = false;
  isExecuting = false;

  selectedTags: ProgramProductTypeTag[] = [];
  canonicalTagId?: string;

  // Last legality result returned by the server. Cleared whenever the canonical
  // selection changes — admin must re-check before they can Consolidate again.
  legality?: TagConsolidationLegalityDto;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IConsolidateTagsParams>,
    private toastr: ToastrService,
    private dbQueryService: AccountDbQueryService,
    private dbSaveService: AccountDbSaveService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);
    this.selectedTags = [...(params.selectedTags ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    this.isPageReady = true;
  }

  public static async openDialog(
    dialogService: DialogService,
    data: IConsolidateTagsParams,
    config?: DynamicDialogConfig<IConsolidateTagsParams>,
  ): Promise<IConsolidateTagsResult | undefined> {
    const ref = dialogService.open(ConsolidateTagsDialogComponent, {
      closable: false,
      draggable: true,
      modal: true,
      width: 'max(55vw, 640px)',
      height: 'auto',
      focusOnShow: false,
      ...config,
      data,
    });
    return await firstValueFrom(ref.onClose);
  }

  onCanonicalChange() {
    // Selection changed — last legality result no longer applies.
    this.legality = undefined;
  }

  synonymTagIds(): string[] {
    if (!this.canonicalTagId) return [];
    return this.selectedTags.filter((t) => t.id !== this.canonicalTagId).map((t) => t.id);
  }

  canCheckLegality(): boolean {
    return !!this.canonicalTagId && !this.isCheckingLegality && !this.isExecuting && this.selectedTags.length >= 2;
  }

  canConsolidate(): boolean {
    return !!this.legality?.IsLegal && !this.isCheckingLegality && !this.isExecuting;
  }

  async onCheckLegality() {
    if (!this.canCheckLegality()) return;
    this.isCheckingLegality = true;
    try {
      this.legality = await this.dbQueryService.checkTagConsolidationLegality(this.canonicalTagId!, this.synonymTagIds());
    } catch (e) {
      this.toastr.error('Could not check consolidation legality.');
      throw e;
    } finally {
      this.isCheckingLegality = false;
    }
  }

  async onConsolidate() {
    if (!this.canConsolidate()) return;
    this.isExecuting = true;
    try {
      const result: TagConsolidationResultDto = await this.dbSaveService.consolidateProductTypeTags(
        this.canonicalTagId!,
        this.synonymTagIds(),
      );
      const canonicalName = this.legality?.CanonicalTagName ?? '(canonical)';
      this.toastr.success(
        `Consolidated ${result.SynonymTagsDeleted} tag(s) into '${canonicalName}'. ` +
          `${result.IssuanceRewrites} issuance(s) and ${result.ProductTypeMapRewrites} product mapping(s) updated.`,
        'Tags Consolidated',
      );
      this.ref.close({ consolidated: true, canonicalTagId: this.canonicalTagId } as IConsolidateTagsResult);
    } catch (e) {
      this.toastr.error('Failed to consolidate tags. The transaction was rolled back.');
      this.isExecuting = false;
      throw e;
    }
  }

  onCancel() {
    this.ref.close({ consolidated: false } as IConsolidateTagsResult);
  }
}
